import { useQuery } from '@tanstack/react-query'

import { retryTransientRead } from '../../services/api/client'

import type { ProviderResourceRepository } from '../settings/provider-resource-repository'
import type { ServiceBookingSettings } from '../settings/provider-resource-contracts'

export type BookingCapabilityState =
  | 'ready'
  | 'provider-gated'
  | 'unsupported'
  | 'temporarily-unavailable'
  | 'unknown'
  | 'loading'

export type BookingCapability = {
  state: BookingCapabilityState
  bookAllowed: boolean
  appointmentActionsAllowed: boolean
  bookingMessage: string
  appointmentActionsMessage: string
}

const blocked = (
  state: Exclude<BookingCapabilityState, 'ready' | 'provider-gated'>,
  bookingMessage: string
): BookingCapability => ({
  state,
  bookAllowed: false,
  appointmentActionsAllowed: false,
  bookingMessage,
  appointmentActionsMessage: bookingMessage,
})

export function bookingCapabilityFromSettings(
  settings: ServiceBookingSettings
): BookingCapability {
  if (settings.status === 'AUTHORITY_UNAVAILABLE') {
    return blocked(
      'unsupported',
      'Appointment booking is not supported for this clinic.'
    )
  }
  if (settings.status === 'RESOURCE_REFRESH_REQUIRED') {
    return blocked(
      'temporarily-unavailable',
      'Appointment booking is unavailable until scheduling resources refresh.'
    )
  }
  if (settings.status === 'REVIEW_REQUIRED') {
    return blocked(
      'temporarily-unavailable',
      'Appointment booking is unavailable until scheduling setup is reviewed.'
    )
  }
  if (!settings.bookingReadiness) {
    return blocked(
      'temporarily-unavailable',
      'Appointment booking is unavailable until scheduling setup is complete.'
    )
  }
  if (settings.bookingReadiness.decision === 'PROVIDER_GATED') {
    return {
      state: 'provider-gated',
      bookAllowed: true,
      appointmentActionsAllowed: false,
      bookingMessage: 'Booking uses live provider validation.',
      appointmentActionsMessage:
        'Live provider validation authorizes new bookings only; appointment changes are unavailable.',
    }
  }
  return {
    state: 'ready',
    bookAllowed: true,
    appointmentActionsAllowed: true,
    bookingMessage: 'Appointment booking is ready.',
    appointmentActionsMessage: 'Appointment changes are available.',
  }
}

// The same query Settings > Services reads and updates on save, so the two
// always agree. A recent answer is shown at once on every visit (the detail
// dialog too) instead of "Checking…" until it is asked again; it is still
// refreshed every 30 s while shown, as before.
export const BOOKING_CAPABILITY_REFRESH_MS = 30_000

export function useBookingCapability(
  repository: ProviderResourceRepository | undefined,
  tenantId: string
): BookingCapability {
  const canQuery = Boolean(repository?.getServiceBooking && tenantId)
  const query = useQuery({
    queryKey: ['settings', tenantId, 'service-booking'],
    queryFn: () => repository!.getServiceBooking!(tenantId),
    enabled: canQuery,
    staleTime: BOOKING_CAPABILITY_REFRESH_MS,
    refetchInterval: BOOKING_CAPABILITY_REFRESH_MS,
    retry: retryTransientRead,
  })

  // A read that failed is never answered from an older success: booking stays
  // blocked until the capability is verified again.
  if (!canQuery || query.isError) {
    return blocked(
      'unknown',
      'Appointment booking capability could not be verified.'
    )
  }
  if (!query.data) {
    return blocked('loading', 'Checking appointment booking availability…')
  }
  return bookingCapabilityFromSettings(query.data)
}
